import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import { useNavigate, useParams } from "react-router-dom";
import { getCategoryName, getInitialDishName, getSubCategoryName, validationCloneDishSchema } from "../constants";
import { useGetCurrentMenuItemLazyQuery, useGetMenuCategoriesQuery } from "@me-team/host/main/menu/graphql/menu.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess, MenuItemCloneInput } from "@me-team/host/main/graphql/types";
import ModalCloneMenuItemFormEffects from "./ModalCloneMenuItemFormEffects";
import ErrorService from "../../../../services/ErrorService";
import { normalizeCategoryData } from "../../../../services/servicesMeMenuCategories/normalizeCategoryData";
import { cloneNameToApi, cloneNameToDisplay } from "../../utils/cloneNameUtils";
import { PartialMenuCategory, PartialMenuItem } from "../../../../interfaces/meMenuInterfaces/interfaces";
import { useCloneMenuItemInMenuContentMutation } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { EditMenuService } from "../../../MenuEditContentPage/service/editMenuService";
import TranslationMissingDot from "../../components/LanguageVersionSelect/TranslationMissingDot";
import { buildTranslationTextVm } from "../../utils/translationVersions/buildTranslationTextVm";

interface ModalCloneMenuItemProps {
    showCloneModal: boolean;
    setShowCloneModal: React.Dispatch<React.SetStateAction<boolean>>;
    currentDishId: number;
    refetchMenuItems?: () => void;
    onCloseModal: () => void;
    localeVersion?: string;
    headerLanguage?: string;
    canCloneDish?: boolean;
    isTranslationMode?: boolean;
}

interface DataCloneForm {
    name: string;
    categories: number[];
}

const ModalCloneMenuItem: React.FC<ModalCloneMenuItemProps> = ({
    showCloneModal,
    setShowCloneModal,
    currentDishId,
    refetchMenuItems,
    onCloseModal,
    localeVersion,
    headerLanguage,
    canCloneDish,
    isTranslationMode = false,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const canCloneDishByRole = useRoleAccess(CompanyAccess.MenuDishClone);
    const canSubmitClone = canCloneDish ?? canCloneDishByRole;

    const [getCurrentMenuItem, { data: currentMenuItemData, refetch: currentMenuItemRefetch }] =
        useGetCurrentMenuItemLazyQuery();
    const [cloneMenuItemInMenuContent] = useCloneMenuItemInMenuContentMutation();

    useEffect(() => {
        currentDishId &&
            getCurrentMenuItem({
                variables: { id: currentDishId, companyId: Number(currentCompanyId), locale: localeVersion },
            });
    }, [currentDishId, currentCompanyId, localeVersion, getCurrentMenuItem]);

    const menuItemData: PartialMenuItem = currentMenuItemData?.user?.company?.menuItems?.items[0];

    const categoryData: PartialMenuCategory = menuItemData?.categories[0];
    const startCategoryId = categoryData?.id;
    const startCategoryName = getCategoryName(categoryData);
    const startSubCategoryName = getSubCategoryName(categoryData);
    const menuItemNameVm = buildTranslationTextVm({
        translatedValue: menuItemData?.name,
        originValue: menuItemData?.nameOrigin,
        isTranslationMode,
    });
    const initialDishName = getInitialDishName(menuItemNameVm.displayText, t);

    const [selectedCategory, setSelectedCategory] = useState<ReturnType<
        typeof EditMenuService.categoryModuleCreator
    > | null>(null);

    const [currentCatName, setCurrentCatName] = useState({
        categoryName: startCategoryName,
        subCategoryName: startSubCategoryName,
    });

    const [pickedCategoryId, setPickedCategoryId] = useState<number[] | null>([startCategoryId]);

    useEffect(() => {
        if (startCategoryId) {
            setPickedCategoryId([startCategoryId]);
            setCurrentCatName({
                categoryName: startCategoryName,
                subCategoryName: startSubCategoryName ? startSubCategoryName : "",
            });
        }
    }, [startCategoryId, startCategoryName, startSubCategoryName, menuItemData?.name, t]);

    const { data: meMenuCategoriesData } = useGetMenuCategoriesQuery({
        variables: {
            companyId: Number(currentCompanyId),
            itemsPerPage: 1000,
            isDeleted: false,
            locale: localeVersion,
        },
        skip: !currentCompanyId || !localeVersion,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setSelectedCategory(EditMenuService.categoryModuleCreator(normalizedItems, null, null, isTranslationMode));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLInputElement;

            if (
                (target === inputRef.current && isPickerOpen) ||
                (dropdownRef.current && !dropdownRef.current.contains(target))
            ) {
                setIsPickerOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isPickerOpen]);

    const handleCategoryNameChange = (categoryName: string) => {
        setCurrentCatName({ categoryName, subCategoryName: "" });
    };
    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string = "") => {
        setCurrentCatName({ categoryName, subCategoryName });
    };

    const initialValues: DataCloneForm = {
        name: initialDishName,
        categories: pickedCategoryId ? pickedCategoryId : null,
    };

    const handleCloneDish = async (values: DataCloneForm, { setErrors }: FormikHelpers<DataCloneForm>) => {
        if (!canSubmitClone) return;
        if (!currentDishId) return;

        const normalizedName = cloneNameToApi(values?.name, t);

        const input: MenuItemCloneInput = {
            name: normalizedName,
            category: values?.categories[0],
        };

        await cloneMenuItemInMenuContent({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                cloneMenuItemId: currentDishId,
                input,
                locale: localeVersion,
            },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                const newDishId = data?.cloneMenuItemNew.id;
                setShowCloneModal(false);
                currentMenuItemRefetch();
                refetchMenuItems && refetchMenuItems();
                navigate(`/menu/update-dish/dish/${newDishId}/branch/${branchId}`);
            },
            onError: (error: ApolloError) => {
                const validationErrors = error?.graphQLErrors[0]?.extensions?.category;
                if (validationErrors === "menu_item.already_exists") {
                    setErrors({ name: t("A dish with this name already exists.") });
                }
                console.error("error", error);
            },
        });
    };

    return (
        <>
            <Modal centered show={showCloneModal} onHide={onCloseModal} size="lg">
                <Modal.Header closeButton className="border-bottom-0">
                    <Modal.Title className="fs-5">
                        {t("Cloning")} {cloneNameToDisplay(menuItemNameVm.displayText, t)}
                        {menuItemNameVm.isMissingTranslation ? (
                            <TranslationMissingDot tooltipText={t("Edit the language version")} />
                        ) : null}
                    </Modal.Title>
                    <div className="d-flex justify-content-center align-items-center bg-violet-light rounded-1 p-1 px-6 ms-3">
                        <span className="fs-7 text-primary">{headerLanguage}</span>
                    </div>
                </Modal.Header>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationCloneDishSchema(t)}
                    onSubmit={handleCloneDish}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ setFieldTouched, values, touched, errors, handleChange, handleBlur }) => {
                        return (
                            <Form>
                                <ModalCloneMenuItemFormEffects
                                    pickedCategoryIds={pickedCategoryId}
                                    menuItemName={menuItemNameVm.displayText}
                                />
                                <Modal.Body>
                                    <div className="form-group mb-3">
                                        <Row className="align-items-center">
                                            <Col sm={3} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="name"
                                                    className="fs-7 fw-bold text-start required-label"
                                                >
                                                    {t("Name dish")}
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    className={`fs-7 form-control rounded-1 ${touched.name && errors.name ? "is-invalid" : ""}`}
                                                    id="cloneDish-name-field"
                                                    value={values.name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder={t("Enter the name of the dish")}
                                                    maxLength={100}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={9} className="ms-auto">
                                                <ErrorMessage
                                                    name="name"
                                                    component="div"
                                                    className="fs-7 mt-1 text-danger"
                                                />
                                                <div className="fs-7 text-grey mt-1">{values.name?.length}/100</div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div className="form-group">
                                        <Row className="align-items-center">
                                            <Col sm={3} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="cloneDish-category-field"
                                                    className="fs-7 fw-bold required-label"
                                                >
                                                    {t("Category")}
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        name="categories"
                                                        id="cloneDish-category-field"
                                                        className={`form-control  ps-6 cursor-pointer ${touched.categories && errors.categories ? "category-input-invalid is-invalid" : ""}`}
                                                        placeholder={t("Category")}
                                                        value={
                                                            currentCatName?.subCategoryName
                                                                ? currentCatName.subCategoryName
                                                                : currentCatName?.categoryName || ""
                                                        }
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setIsPickerOpen((prev) => !prev);
                                                        }}
                                                        onBlur={() => setFieldTouched("categories", true)}
                                                        readOnly
                                                        ref={inputRef}
                                                    />
                                                    <i
                                                        className={`bi bi-chevron-down position-absolute end-0 text-dark me-2 pointer-event-none transition-transform-icon ${isPickerOpen ? "rotate-180-icon" : ""}`}
                                                        style={{ top: "54%" }}
                                                    ></i>
                                                    <div
                                                        ref={dropdownRef}
                                                        className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-1 bg-white shadow overflow-hidden"
                                                        style={{ maxHeight: "320px" }}
                                                    >
                                                        {meMenuCategoriesData && selectedCategory && (
                                                            <CategoryPickerComponent
                                                                isOpen={isPickerOpen}
                                                                i18n={i18next}
                                                                onSearchChange={() => {}}
                                                                showMissingTranslationDot={isTranslationMode}
                                                                renderMissingTranslationDot={() => (
                                                                    <TranslationMissingDot
                                                                        tooltipText={t("Edit the language version")}
                                                                    />
                                                                )}
                                                                onChangeCategory={(
                                                                    currentCategory: CategoryPickerCategory
                                                                ) => {
                                                                    if (currentCategory.isPicked) {
                                                                        handleCategoryNameChange(currentCategory?.name);
                                                                        setPickedCategoryId([currentCategory?.id]);
                                                                        setIsPickerOpen(false);
                                                                        setFieldTouched("categories", true);
                                                                    } else {
                                                                        setCurrentCatName({
                                                                            categoryName: "",
                                                                            subCategoryName: "",
                                                                        });
                                                                        setPickedCategoryId(null);
                                                                        setFieldTouched("categories", true);
                                                                    }
                                                                }}
                                                                onChangeSubcategory={(currentSubcategory) => {
                                                                    if (currentSubcategory?.isPicked) {
                                                                        handleSubCategoryNameChange(
                                                                            currentSubcategory?.name
                                                                        );
                                                                        setPickedCategoryId([currentSubcategory?.id]);
                                                                        setIsPickerOpen(false);
                                                                        setFieldTouched("categories", true);
                                                                    } else {
                                                                        setCurrentCatName({
                                                                            categoryName: "",
                                                                            subCategoryName: "",
                                                                        });
                                                                        setPickedCategoryId(null);
                                                                        setFieldTouched("categories", true);
                                                                    }
                                                                }}
                                                                serviceCategories={selectedCategory}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col sm={9} className="ms-auto">
                                                <ErrorMessage name="categories">
                                                    {(msg) => <div className="fs-7 mt-1 text-danger">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer className="justify-content-between border-top-0 flex-nowrap">
                                    <Button variant="outline-primary" className="custom-btn" onClick={onCloseModal}>
                                        {t("cancel")}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="custom-btn fw-bold"
                                        type="submit"
                                        disabled={!canSubmitClone}
                                    >
                                        {t("Clone")}
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal>

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default ModalCloneMenuItem;
